// app/components/common/Input/Input.tsx
'use client';

import * as React from 'react';
import { useState } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Icon } from '../Icon';
import { Label } from '../Label';
import { IconMap } from '../IconMap';
import { cn } from '@workspace/lib';
import { theme } from '../../theme';

const container = cva('group', {
  variants: {
    kind: {
      default: 'relative max-w-[240px] h-[48px]',
      field: 'flex items-center gap-2 px-3 py-2 rounded-sm border transition-all duration-200 w-60 max-w-[240px] !h-11',
      defined: 'flex items-center rounded-sm border h-[2.5rem]',
    },
    disabled: { true: 'cursor-not-allowed', false: '' },
    hasFocusStyles: { true: 'focus-within:border-1', false: '' },
    status: {
      default: '',
      error: 'border-error',
      success: 'border-success',
    },
    site: {
      porta: '',
      portaNZ: '',
      redRec: '',
      easycraft: '',
    },
    widthKey: {
      w0: 'max-w-[240px]',
      w1: 'max-w-[288px]',
      w2: 'max-w-[338px]',
      w3: 'max-w-[386px]',
    },
  },
  compoundVariants: [
    {
      kind: 'field',
      disabled: false,
      status: 'default',
      site: ['porta', 'portaNZ', 'easycraft'],
      class:
        'border-secondary-100 hover:bg-secondary-50 focus-within:border-secondary-200 focus-within:shadow-[0_0_0_2px_white,0_0_0_4px_theme(colors.primary.500)]',
    },
    {
      kind: 'field',
      disabled: false,
      status: 'default',
      site: 'redRec',
      class:
        'border-primary hover:bg-secondary-50 focus-within:border-secondary-200 focus-within:shadow-[0_0_0_2px_white,0_0_0_4px_theme(colors.primary.500)]',
    },
    {
      kind: 'field',
      disabled: false,
      status: 'error',
      class:
        'hover:bg-secondary-50 focus-within:border-secondary-200 focus-within:shadow-[0_0_0_2px_white,0_0_0_4px_theme(colors.primary.500)] focus-within:!border-sm !border-md',
    },
    {
      kind: 'field',
      disabled: false,
      status: 'success',
      class:
        'hover:bg-secondary-50 focus-within:border-secondary-200 focus-within:shadow-[0_0_0_2px_white,0_0_0_4px_theme(colors.primary.500)]',
    },
    {
      kind: 'field',
      disabled: true,
      class: 'bg-secondary-100 border-secondary-200',
    },
    {
      kind: 'defined',
      disabled: false,
      class:
        'border-secondary-200 focus-within:border-secondary-200 focus-within:shadow-[0_0_0_2px_white,0_0_0_4px_theme(colors.red.400)]',
    },
    {
      kind: 'defined',
      disabled: true,
      class: 'border-secondary-200 bg-secondary-100',
    },
  ],
  defaultVariants: {
    kind: 'default',
    disabled: false,
    hasFocusStyles: true,
    status: 'default',
    site: 'porta',
    widthKey: 'w0',
  },
});

const mainRow = cva('flex items-center gap-2 h-[40px] px-2 py-3 border-t border-b', {
  variants: {
    disabled: {
      true: 'bg-secondary-100 border-secondary-200',
      false: 'border-secondary-200 group-hover:bg-accent',
    },
    widthKey: { w240: 'w-[240px]', w288: 'w-[288px]', w338: 'w-[338px]' },
  },
  defaultVariants: { disabled: false, widthKey: 'w240' },
});

const tabSide = cva('flex items-center justify-center h-[40px] px-2 py-3', {
  variants: {
    side: { left: 'w-[98px] border-r', right: 'w-[48px] border-l' },
    disabled: {
      true: 'border-secondary-200 bg-secondary-50',
      false: 'border-secondary-200',
    },
  },
  defaultVariants: { side: 'left', disabled: false },
});

const inputField = cva('placeholder:text-secondary-200 text-secondary-900 w-full', {
  variants: {
    kind: {
      default: 'flex rounded-sm p-[12px] pr-10',
      field: 'bg-transparent outline-none',
      defined: 'bg-transparent outline-none',
    },
    disabled: { true: 'cursor-not-allowed', false: '' },
    filled: { true: '', false: 'text-secondary-800' },
    hasIconLeft: { true: 'pl-[44px]', false: '' },
    isPasswordDefault: {
      true: 'focus:outline-2 focus:outline-offset-2 focus:outline-primary-500',
      false: '',
    },
  },
  compoundVariants: [
    {
      kind: 'default',
      disabled: false,
      class: 'border border-secondary-900 hover:border-gray-900 hover:bg-secondary-100 rounded-sm',
    },
    {
      kind: 'default',
      disabled: true,
      class: 'bg-secondary-100 border-secondary-200',
    },
  ],
  defaultVariants: {
    kind: 'default',
    disabled: false,
    filled: false,
    hasIconLeft: false,
    isPasswordDefault: false,
  },
});

const iconTone = cva('', {
  variants: {
    disabled: { true: 'text-secondary-200 cursor-not-allowed', false: '' },
    status: {
      default: 'text-black',
      error: 'text-error-300',
      success: 'text-success',
    },
  },
  defaultVariants: { disabled: false, status: 'default' },
});

const preTabPlaceholder = cva('placeholder:text-secondary-200', {
  variants: {
    disabled: {
      true: 'text-secondary-200',
      false: '',
    },
    status: {
      default: '',
      error: 'placeholder:text-error-200',
      success: 'placeholder:text-success',
    },
  },
  defaultVariants: { disabled: false, status: 'default' },
});

type ContainerV = VariantProps<typeof container>;
type MainRowV = VariantProps<typeof mainRow>;

interface InputProps {
  id?: string;
  placeholder?: string;
  label?: string;
  type?: 'text' | 'password' | 'email' | 'tel';
  maxLength?: number;
  iconLeft?: keyof typeof IconMap;
  iconRight?: keyof typeof IconMap;
  iconSize?: number;
  value?: string;
  onChange?: (value: string) => void;
  isDisabled?: boolean;
  status?: 'default' | 'error' | 'success';
  variant?: 'default' | 'field' | 'defined-field';
  showPreTab?: boolean;
  preTabIcon?: keyof typeof IconMap;
  onPreTabChange?: (value: string) => void;
  showPostTab?: boolean;
  postTabIcon?: keyof typeof IconMap;
  onPostTabClick?: () => void;
  className?: string;
  inputClassName?: string;
  onFocus?: () => void;
  onBlur?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  isRequired?: boolean;
  name?: string;
  autoComplete?: string;
}

export function Input({
  id,
  placeholder = 'Enter text...',
  label,
  type = 'text',
  maxLength,
  iconLeft,
  iconRight,
  iconSize = 24,
  value,
  onChange,
  isDisabled = false,
  status = 'default',
  variant = 'default',
  showPreTab = false,
  preTabIcon,
  onPreTabChange,
  showPostTab = false,
  postTabIcon,
  onPostTabClick,
  className = '',
  inputClassName = '',
  onFocus,
  onBlur,
  onKeyDown,
  isRequired,
  name,
  autoComplete,
}: InputProps) {
  const [internalValue, setInternalValue] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const currentValue = value ?? internalValue;
  const isFilled = currentValue.length > 0;
  const effectiveType = type === 'password' && showPassword ? 'text' : type;

  const handleChange = (val: string) => (onChange ? onChange(val) : setInternalValue(val));
  const handleTogglePassword = () => {
    if (!isDisabled && type === 'password' && iconRight) setShowPassword((s) => !s);
  };

  const shouldRenderIconRight = iconRight && (type !== 'password' || variant === 'default');

  const containerWidthKey: NonNullable<ContainerV['widthKey']> =
    variant === 'defined-field'
      ? !showPreTab && !showPostTab
        ? 'w0'
        : !showPreTab
          ? 'w1'
          : !showPostTab
            ? 'w2'
            : 'w3'
      : 'w0';

  const mainRowWidthKey: NonNullable<MainRowV['widthKey']> =
    showPreTab && showPostTab ? 'w240' : showPreTab ? 'w288' : showPostTab ? 'w338' : 'w240';

  if (variant === 'defined-field') {
    return (
      <div
        className={cn(
          container({
            kind: 'defined',
            disabled: isDisabled,
            hasFocusStyles: !isDisabled,
            widthKey: containerWidthKey,
          }),
          className,
        )}
      >
        {showPreTab && (
          <div className={tabSide({ side: 'left', disabled: isDisabled })}>
            {preTabIcon && (
              <Icon
                name={preTabIcon}
                size={24}
                className={cn('w-6 h-6 mr-1', iconTone({ disabled: isDisabled, status }))}
              />
            )}
            <input
              type="text"
              placeholder="www."
              disabled={isDisabled}
              onChange={(e) => onPreTabChange?.(e.target.value)}
              onFocus={onFocus}
              onBlur={onBlur}
              className={cn(
                'w-full outline-none text-sm text-center font-heading',
                preTabPlaceholder({ disabled: isDisabled, status }),
              )}
            />
          </div>
        )}

        <div
          className={mainRow({
            disabled: isDisabled,
            widthKey: mainRowWidthKey,
          })}
        >
          {iconLeft && (
            <Icon
              name={iconLeft}
              size={24}
              className={cn('w-6 h-6 mr-1', iconTone({ disabled: isDisabled, status }))}
            />
          )}
          <input
            id={id}
            type={effectiveType}
            value={currentValue}
            placeholder={placeholder}
            maxLength={maxLength}
            disabled={isDisabled}
            name={name}
            autoComplete={autoComplete}
            onChange={(e) => handleChange(e.target.value)}
            onFocus={onFocus}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            className={cn(
              inputField({
                kind: 'defined',
                disabled: isDisabled,
                filled: isFilled,
                hasIconLeft: !!iconLeft,
              }),
              inputClassName,
            )}
          />
          {shouldRenderIconRight && (
            <Icon
              name={iconRight}
              size={24}
              className={cn('w-6 h-6 ml-1 pointer-events-none', iconTone({ disabled: isDisabled, status }))}
            />
          )}
        </div>

        {showPostTab && (
          <div
            className={tabSide({ side: 'right', disabled: isDisabled })}
            onClick={onPostTabClick}
            tabIndex={isDisabled ? -1 : 0}
          >
            {postTabIcon && (
              <Icon
                name={postTabIcon}
                size={24}
                className={cn('w-6 h-6', iconTone({ disabled: isDisabled, status }))}
              />
            )}
          </div>
        )}
      </div>
    );
  }

  if (variant === 'field') {
    return (
      <div
        className={cn(
          container({
            kind: 'field',
            disabled: isDisabled,
            status,
            site: theme,
          }),
          className,
        )}
      >
        {iconLeft && (
          <Icon
            name={iconLeft}
            size={iconSize}
            className={cn('mr-1', iconTone({ disabled: isDisabled, status }))}
            style={{ width: iconSize, height: iconSize }}
          />
        )}
        <input
          id={id}
          type={effectiveType}
          value={currentValue}
          placeholder={placeholder}
          maxLength={maxLength}
          disabled={isDisabled}
          name={name}
          autoComplete={autoComplete}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          className={cn(
            inputField({
              kind: 'field',
              disabled: isDisabled,
              filled: isFilled,
            }),
            inputClassName,
          )}
        />
        {shouldRenderIconRight && (
          <Icon
            name={iconRight}
            size={iconSize}
            className={cn('ml-1 pointer-events-none', iconTone({ disabled: isDisabled, status }))}
            style={{ width: iconSize, height: iconSize }}
          />
        )}
      </div>
    );
  }

  return (
    <div className={cn(container({ kind: 'default' }), className)}>
      {label && (
        <Label
          htmlFor={id}
          text={label}
          isRequired={isRequired}
          className="block text-sm font-medium text-gray-700 mb-1"
        />
      )}

      <div className="relative">
        {iconLeft && (
          <Icon
            name={iconLeft}
            size={20}
            className={cn(
              'w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2',
              iconTone({ disabled: isDisabled, status }),
            )}
          />
        )}

        <input
          id={id}
          type={effectiveType}
          placeholder={placeholder}
          value={currentValue}
          maxLength={maxLength}
          name={name}
          autoComplete={autoComplete}
          onChange={(e) => handleChange(e.target.value)}
          disabled={isDisabled}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          className={cn(
            inputField({
              kind: 'default',
              disabled: isDisabled,
              filled: isFilled,
              hasIconLeft: !!iconLeft,
              isPasswordDefault: type === 'password',
            }),
            inputClassName,
          )}
        />

        {iconRight && type === 'password' && (
          <Icon
            name="Eye"
            size={20}
            className={cn(
              'w-5 h-5 absolute right-3 top-1/2 -translate-y-1/2',
              iconTone({ disabled: isDisabled, status }),
            )}
            onClick={handleTogglePassword}
          />
        )}
      </div>
    </div>
  );
}
